import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Check,
  Clapperboard,
  Download,
  ExternalLink,
  Film,
  Loader2,
  Lock,
  PauseCircle,
  Play,
  Search,
  Sparkles,
  Trash2,
  Video,
  Wand2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useMockups } from '../../hooks/useSupabase';
import {
  CreateVideoMockupRequest,
  VideoMockup,
  videoMockupService,
} from '../../services/videoMockupService';
import { API_BASE_URL } from '../../utils/apiUrl';
import { hasProfessionalAccess } from '../../utils/licensing';

const PROMPT_STARTERS = [
  'Add a slow cinematic camera push with soft light movement and premium product focus.',
  'Create a luxury reveal with subtle reflections, light sweeps, and calm depth.',
  'Animate the scene with energetic parallax, confident camera movement, and ad-style polish.',
  'Create a clean social ad loop with smooth motion, premium pacing, and sharp branding.',
];

const QUALITY_OPTIONS: Array<{
  value: CreateVideoMockupRequest['model'];
  label: string;
  hint: string;
}> = [
  { value: 'viduq3-turbo', label: 'Fast', hint: 'Quicker turnaround for iteration' },
  { value: 'viduq3-pro', label: 'Premium', hint: 'Higher-end output for final renders' },
];

const DURATION_OPTIONS = [5, 8, 12];
const RESOLUTION_OPTIONS: Array<CreateVideoMockupRequest['resolution']> = ['720p', '1080p'];
const PROCESSING_STATES = new Set(['created', 'queueing', 'processing']);

const resolveMediaUrl = (url?: string | null) => {
  if (!url) {
    return '';
  }

  return url.startsWith('http://') || url.startsWith('https://') ? url : `${API_BASE_URL}${url}`;
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'created':
      return 'Queued';
    case 'queueing':
      return 'Queueing';
    case 'processing':
      return 'Rendering';
    case 'success':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
};

const VideoMockups: React.FC = () => {
  const { profile } = useAuth();
  const { mockups, loading: mockupsLoading } = useMockups();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState<CreateVideoMockupRequest['model']>('viduq3-turbo');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState<CreateVideoMockupRequest['resolution']>('720p');
  const [submitting, setSubmitting] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [videoMockups, setVideoMockups] = useState<VideoMockup[]>([]);
  const [pageError, setPageError] = useState<string | null>(null);
  const [previewTarget, setPreviewTarget] = useState<VideoMockup | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(true);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  const hasProfessionalLicense = hasProfessionalAccess(profile);

  const filteredMockups = useMemo(() => {
    const searchValue = searchQuery.trim().toLowerCase();
    if (!searchValue) {
      return mockups;
    }

    return mockups.filter((mockup) =>
      mockup.name.toLowerCase().includes(searchValue)
      || (mockup.template?.name || mockup.template_name || '').toLowerCase().includes(searchValue)
    );
  }, [mockups, searchQuery]);

  const selectedSource = useMemo(
    () => mockups.find((mockup) => mockup.id === selectedSourceId) || null,
    [mockups, selectedSourceId]
  );

  const activeJobs = useMemo(
    () => videoMockups.filter((item) => PROCESSING_STATES.has(item.status)),
    [videoMockups]
  );

  useEffect(() => {
    const sourceId = searchParams.get('source');
    if (!sourceId || selectedSourceId || mockups.length === 0) {
      return;
    }

    const matchedMockup = mockups.find((mockup) => mockup.id === sourceId);
    if (matchedMockup) {
      setSelectedSourceId(matchedMockup.id);
      setPrompt('Create a premium short-form video from this mockup with polished camera movement and refined commercial motion.');
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('source');
    setSearchParams(nextParams, { replace: true });
  }, [mockups, searchParams, selectedSourceId, setSearchParams]);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        setLoadingJobs(true);
        const data = await videoMockupService.list();
        setVideoMockups(data);
      } catch (error: any) {
        setPageError(error.response?.data?.error?.message || error.message || 'Failed to load video mockups.');
      } finally {
        setLoadingJobs(false);
      }
    };

    void loadJobs();
  }, []);

  useEffect(() => {
    if (activeJobs.length === 0) {
      return;
    }

    const interval = window.setInterval(async () => {
      try {
        const refreshed = await Promise.all(activeJobs.map((item) => videoMockupService.get(item.id)));
        setVideoMockups((current) =>
          current.map((item) => refreshed.find((candidate) => candidate.id === item.id) || item)
        );
      } catch (error) {
        console.error('Failed to refresh video mockup jobs:', error);
      }
    }, 5000);

    return () => window.clearInterval(interval);
  }, [activeJobs]);

  const handleSubmit = async () => {
    if (!selectedSourceId) {
      setPageError('Choose a source mockup first.');
      return;
    }

    if (!prompt.trim()) {
      setPageError('Describe how the mockup should move on screen.');
      return;
    }

    setPageError(null);
    setSubmitting(true);

    try {
      const nextVideoMockup = await videoMockupService.create({
        sourceMockupId: selectedSourceId,
        prompt: prompt.trim(),
        model,
        duration,
        resolution,
      });

      setVideoMockups((current) => [nextVideoMockup, ...current]);
      showNotification('success', 'Video render started', 'Your Video Mockup is now rendering.');
    } catch (error: any) {
      setPageError(error.response?.data?.error?.message || error.message || 'Failed to start video generation.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (videoMockup: VideoMockup) => {
    if (!window.confirm(`Delete "${videoMockup.name}"?`)) {
      return;
    }

    setDeletingId(videoMockup.id);
    try {
      await videoMockupService.remove(videoMockup.id);
      setVideoMockups((current) => current.filter((item) => item.id !== videoMockup.id));
    } catch (error: any) {
      setPageError(error.response?.data?.error?.message || error.message || 'Failed to delete the video mockup.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (videoMockup: VideoMockup) => {
    setDownloadingId(videoMockup.id);
    try {
      await videoMockupService.download(videoMockup);
    } catch (error: any) {
      setPageError(error.response?.data?.error?.message || error.message || 'Failed to download the video mockup.');
    } finally {
      setDownloadingId(null);
    }
  };

  const applyPromptStarter = (starter: string) => {
    setPrompt((current) => (current.trim() ? `${current.trim()} ${starter}` : starter));
  };

  useEffect(() => {
    if (!previewTarget || !previewVideoRef.current) {
      return;
    }

    previewVideoRef.current.currentTime = 0;
    const playPromise = previewVideoRef.current.play();
    if (playPromise) {
      void playPromise
        .then(() => setIsPreviewPlaying(true))
        .catch(() => setIsPreviewPlaying(false));
    }
  }, [previewTarget]);

  const togglePreviewPlayback = async () => {
    if (!previewVideoRef.current) {
      return;
    }

    if (previewVideoRef.current.paused) {
      await previewVideoRef.current.play();
      setIsPreviewPlaying(true);
      return;
    }

    previewVideoRef.current.pause();
    setIsPreviewPlaying(false);
  };

  if (!hasProfessionalLicense) {
    return (
      <div className="min-h-screen bg-slate-50 px-6 py-10">
        <div className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-lg">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 to-red-700">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-slate-900">Video Mockups is a Pro feature</h2>
          <p className="mb-8 text-sm leading-6 text-slate-600">
            Upgrade to Professional or Agency to transform your generated mockups into short premium videos with Vidu.
          </p>
          <button
            onClick={() => navigate('/dashboard/settings?upgrade=pro')}
            className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
          >
            Upgrade License
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-[32px] bg-gradient-to-br from-slate-950 via-slate-900 to-rose-900 text-white shadow-2xl">
          <div className="grid gap-8 px-6 py-8 lg:grid-cols-[1.35fr_0.65fr] lg:px-8">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-rose-100">
                <Clapperboard className="h-3.5 w-3.5" />
                Video Mockups
              </div>
              <h1 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">
                Turn finished mockups into premium product videos with a guided workflow.
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
                Pick an existing mockup, define the motion direction, and let Vidu render a polished short-form video.
                Results are stored inside your account instead of expiring on a third-party link.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Sources</p>
                <p className="mt-3 text-3xl font-bold">{mockups.length}</p>
                <p className="mt-1 text-sm text-slate-300">Available mockups ready to animate.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Video Jobs</p>
                <p className="mt-3 text-3xl font-bold">{videoMockups.length}</p>
                <p className="mt-1 text-sm text-slate-300">History of renders and active jobs.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Workflow</p>
                <p className="mt-3 text-3xl font-bold">3</p>
                <p className="mt-1 text-sm text-slate-300">Select source, guide motion, render output.</p>
              </div>
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.45fr_0.55fr]">
          <section className="space-y-6">
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-700">Step 1</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Choose a source mockup</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Use any generated mockup from your workspace. The selected frame becomes the opening shot for the video.
              </p>

              <div className="relative mt-5">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search your mockups"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-11 py-3 text-sm text-slate-700 outline-none transition focus:border-rose-500 focus:bg-white"
                />
              </div>

              {mockupsLoading ? (
                <div className="mt-5 flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
                </div>
              ) : mockups.length === 0 ? (
                <div className="mt-5 rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-14 text-center">
                  <Film className="mx-auto h-12 w-12 text-slate-400" />
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">Create a mockup first</h3>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
                    Video Mockups starts from an image mockup already in your account. Generate or save one first, then come back here.
                  </p>
                  <button
                    onClick={() => navigate('/dashboard/mockups')}
                    className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white transition hover:bg-slate-800"
                  >
                    Open Templates
                  </button>
                </div>
              ) : (
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredMockups.slice(0, 9).map((mockup) => {
                    const isSelected = selectedSourceId === mockup.id;

                    return (
                      <button
                        key={mockup.id}
                        onClick={() => setSelectedSourceId(mockup.id)}
                        className={`overflow-hidden rounded-3xl border text-left transition ${
                          isSelected
                            ? 'border-rose-500 bg-rose-50 shadow-md shadow-rose-100'
                            : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md'
                        }`}
                      >
                        <div className="aspect-[4/3] bg-slate-100">
                          <img
                            src={resolveMediaUrl(mockup.thumbnail_url || mockup.mockup_url)}
                            alt={mockup.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="space-y-2 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <p className="line-clamp-2 text-sm font-semibold text-slate-900">{mockup.name}</p>
                            {isSelected && <Check className="h-4 w-4 text-rose-700" />}
                          </div>
                          <p className="text-xs text-slate-500">
                            {formatDistanceToNow(new Date(mockup.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-700">Step 2</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Guide the motion</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Describe how the scene should come alive: camera moves, lighting changes, atmosphere, reveal style, and pacing.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {PROMPT_STARTERS.map((starter) => (
                  <button
                    key={starter}
                    onClick={() => applyPromptStarter(starter)}
                    className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                  >
                    {starter}
                  </button>
                ))}
              </div>

              <textarea
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder="Example: Start on the mockup hero angle, add a slow camera orbit, subtle light sweep across the surface, and a premium editorial finish with smooth depth and polished movement."
                className="mt-5 min-h-[170px] w-full rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm leading-6 text-slate-800 outline-none transition focus:border-rose-500 focus:bg-white"
              />

              <div className="mt-8 grid gap-6 lg:grid-cols-3">
                <div>
                  <p className="mb-3 text-sm font-semibold text-slate-800">Quality mode</p>
                  <div className="space-y-2">
                    {QUALITY_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setModel(option.value)}
                        className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                          model === option.value
                            ? 'border-rose-500 bg-rose-50'
                            : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="block text-sm font-semibold text-slate-900">{option.label}</span>
                        <span className="mt-1 block text-xs text-slate-500">{option.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-sm font-semibold text-slate-800">Duration</p>
                  <div className="grid grid-cols-3 gap-2">
                    {DURATION_OPTIONS.map((option) => (
                      <button
                        key={option}
                        onClick={() => setDuration(option)}
                        className={`rounded-2xl border px-4 py-4 text-center transition ${
                          duration === option
                            ? 'border-rose-500 bg-rose-50 text-rose-800'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="block text-lg font-bold">{option}</span>
                        <span className="mt-1 block text-[10px] uppercase tracking-[0.12em] text-slate-500 sm:text-[11px]">
                          seconds
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-3 text-sm font-semibold text-slate-800">Resolution</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RESOLUTION_OPTIONS.map((option) => (
                      <button
                        key={option}
                        onClick={() => setResolution(option)}
                        className={`rounded-2xl border px-4 py-4 text-center transition ${
                          resolution === option
                            ? 'border-rose-500 bg-rose-50 text-rose-800'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="block text-lg font-bold">{option}</span>
                        <span className="text-xs uppercase tracking-[0.16em] text-slate-500">output</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {pageError && (
              <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
                <p>{pageError}</p>
              </div>
            )}

            <div className="flex flex-col gap-4 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Render this video mockup</p>
                <p className="mt-1 text-sm text-slate-600">
                  Source: {selectedSource?.name || 'No source selected'} | {model === 'viduq3-pro' ? 'Premium quality' : 'Fast quality'} | {duration}s | {resolution}
                </p>
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting || !selectedSourceId}
                className={`inline-flex items-center justify-center gap-3 rounded-2xl px-6 py-4 font-semibold transition ${
                  submitting || !selectedSourceId
                    ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Starting render...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    Create Video Mockup
                  </>
                )}
              </button>
            </div>
          </section>

          <aside className="space-y-6 xl:sticky xl:top-6 xl:self-start">
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-700">Wizard Summary</p>
              <h3 className="mt-2 text-xl font-bold text-slate-900">What will be rendered</h3>
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Source</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedSource?.name || 'Select a mockup'}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Motion direction</p>
                  <p className="mt-2 text-sm text-slate-700">
                    {prompt.trim() ? `${prompt.trim().slice(0, 150)}${prompt.trim().length > 150 ? '...' : ''}` : 'Add a motion prompt to guide the render.'}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mode</p>
                    <p className="mt-2 text-xl font-bold text-slate-900">{model === 'viduq3-pro' ? 'Premium' : 'Fast'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Duration</p>
                    <p className="mt-2 text-xl font-bold text-slate-900">{duration}s</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Resolution</p>
                    <p className="mt-2 text-xl font-bold text-slate-900">{resolution}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-700">Tips</p>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                <li className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-rose-700" />
                  Ask for camera direction, lighting changes, and pacing to get a more cinematic result.
                </li>
                <li className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-rose-700" />
                  Use the Premium mode for final campaign-ready motion and the Fast mode for iteration.
                </li>
                <li className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 flex-shrink-0 text-rose-700" />
                  Finished videos are stored in your account so you are not relying on temporary provider URLs.
                </li>
              </ul>
            </div>
          </aside>
        </div>

        <section className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-700">Results</p>
              <h2 className="mt-2 text-2xl font-bold text-slate-900">Your video mockups</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">Track active renders, preview finished videos, and download final MP4s.</p>
            </div>
            <button
              onClick={() => navigate('/dashboard/library')}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
            >
              <ExternalLink className="h-4 w-4" />
              Open Library
            </button>
          </div>

          {loadingJobs ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
            </div>
          ) : videoMockups.length === 0 ? (
            <div className="py-16 text-center">
              <Video className="mx-auto h-12 w-12 text-slate-300" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">No video mockups yet</h3>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
                Select a source mockup and render your first motion asset. Finished videos will appear here.
              </p>
            </div>
          ) : (
            <div className="mt-6 grid gap-5 lg:grid-cols-2 2xl:grid-cols-3">
              {videoMockups.map((item) => {
                const isReady = item.status === 'success' && item.video_url;

                return (
                  <article key={item.id} className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50">
                    <div className="relative aspect-[4/3] bg-slate-200">
                      {isReady ? (
                        <video
                          src={resolveMediaUrl(item.video_url)}
                          poster={resolveMediaUrl(item.thumbnail_url)}
                          className="h-full w-full object-cover"
                          muted
                          loop
                          playsInline
                          onMouseEnter={(event) => void event.currentTarget.play().catch(() => undefined)}
                          onMouseLeave={(event) => {
                            event.currentTarget.pause();
                            event.currentTarget.currentTime = 0;
                          }}
                        />
                      ) : (
                        <img
                          src={resolveMediaUrl(item.thumbnail_url || item.settings?.sourceThumbnailUrl)}
                          alt={item.name}
                          className="h-full w-full object-cover"
                        />
                      )}

                      <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-900 shadow-sm">
                        {statusLabel(item.status)}
                      </div>
                    </div>

                    <div className="space-y-4 p-4">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">{item.name}</h3>
                        <p className="mt-1 text-xs text-slate-500">
                          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })} | {item.duration}s | {item.resolution}
                        </p>
                      </div>

                      {item.error_message && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-3 py-3 text-xs text-red-700">
                          {item.error_message}
                        </div>
                      )}

                      {PROCESSING_STATES.has(item.status) && (
                        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">
                          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                          Render in progress. This card updates automatically.
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            if (isReady) {
                              setPreviewTarget(item);
                              return;
                            }

                            setSelectedSourceId(item.source_mockup_id || '');
                            setPrompt(item.prompt || '');
                            setModel(item.model);
                            setDuration(item.duration);
                            setResolution(item.resolution);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          {isReady ? <Play className="h-4 w-4" /> : <Wand2 className="h-4 w-4" />}
                          {isReady ? 'Preview' : 'Reuse'}
                        </button>
                        <button
                          onClick={() => isReady && handleDownload(item)}
                          disabled={!isReady || downloadingId === item.id}
                          className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                            !isReady || downloadingId === item.id
                              ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                              : 'bg-slate-900 text-white hover:bg-slate-800'
                          }`}
                        >
                          {downloadingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          Download
                        </button>
                      </div>

                      <button
                        onClick={() => handleDelete(item)}
                        disabled={deletingId === item.id}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {deletingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        Delete
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {previewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" onClick={() => setPreviewTarget(null)}>
          <div className="w-full max-w-5xl overflow-hidden rounded-[28px] bg-slate-950 text-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div>
                <p className="text-sm font-semibold">{previewTarget.name}</p>
                <p className="text-xs text-slate-300">{previewTarget.duration}s | {previewTarget.resolution}</p>
              </div>
              <button
                onClick={() => setPreviewTarget(null)}
                className="rounded-full border border-white/10 px-3 py-1 text-sm text-slate-200 transition hover:bg-white/10"
              >
                Close
              </button>
            </div>
            <div className="bg-black p-4">
              <video
                ref={previewVideoRef}
                src={resolveMediaUrl(previewTarget.video_url)}
                poster={resolveMediaUrl(previewTarget.thumbnail_url)}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onPlay={() => setIsPreviewPlaying(true)}
                onPause={() => setIsPreviewPlaying(false)}
                className="max-h-[75vh] w-full rounded-2xl"
              />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-white/10 px-6 py-4">
              <button
                onClick={() => {
                  void togglePreviewPlayback();
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {isPreviewPlaying ? <PauseCircle className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {isPreviewPlaying ? 'Pause Video' : 'Play Video'}
              </button>
              <button
                onClick={() => {
                  void handleDownload(previewTarget);
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-100"
              >
                <Download className="h-4 w-4" />
                Download MP4
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoMockups;
